import {
  Refresh,
  ToggleOn,
  ToggleOffOutlined,
  StoreOutlined,
} from "@mui/icons-material";
import { Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import { AxiosError, TSupplier } from "@/api";
import { FC } from "react";
import {
  AutocompleteInput,
  BooleanField,
  CreateButton,
  Datagrid,
  ExportButton,
  FilterButton,
  FunctionField,
  List,
  ListBase,
  NullableBooleanInput,
  NumberField,
  Pagination,
  ReferenceField,
  ReferenceInput,
  TextField,
  TextInput,
  TopToolbar,
  useGetOne,
  useListContext,
  useListFilterContext,
  useNotify,
  useRecordContext,
  useUpdate,
} from "react-admin";
import { ClearFiltersButton } from "../../components/ClearFiltersButton";
import { ModalImageField } from "../../components/elements/ModalImageField";
import { PRODUCTS, SUPPLIERS } from "../../config/resources";
import { useAdmin } from "../../hooks/admin";
import { exporter } from "./export";
import { ProductsImportButton } from "./import";
import { ProductsListExport } from "./actions/list-export";

const ProductStatusToggle: FC<{ source?: string }> = () => {
  const record = useRecordContext();
  const notify = useNotify();

  const canToggleStatus = record?.status || true; // Since locationType doesn't exist in V4, assume it's always toggleable

  const [update, { isLoading }] = useUpdate(
    PRODUCTS,
    {
      id: record?.id,
      data: { ...record, status: !record.status },
      previousData: record,
    },
    {
      mutationMode: "pessimistic",
      onError: (error: AxiosError) =>
        notify(error.message ?? "Ocurrió un error", { type: "error" }),
    }
  );

  return (
    <IconButton
      color="primary"
      title={record.status ? "Desactivar" : "Activar"}
      onClick={(e) => {
        e.stopPropagation();
        update();
      }}
      disabled={isLoading || !canToggleStatus}
    >
      {isLoading ? (
        <Refresh className={isLoading ? "rotate-icon" : ""} />
      ) : record.status ? (
        <ToggleOn fontSize="large" />
      ) : (
        <ToggleOffOutlined fontSize="large" />
      )}
    </IconButton>
  );
};

const productFilters = [
  <NullableBooleanInput source="status" alwaysOn />,
  <ReferenceInput
    source="supplierId"
    reference={SUPPLIERS}
    sort={{ field: "name", order: "ASC" }}
    perPage={100}
    alwaysOn
  >
    <AutocompleteInput
      label="Proveedor"
      optionText="name"
      filterToQuery={(searchText: string) => ({ name: searchText })}
    />
  </ReferenceInput>,
  // <TextInput source="sku" alwaysOn />,
  <TextInput source="name" label="Buscar..." alwaysOn />,
];

const ProductActions = () => {
  const { hasPermission } = useAdmin();
  const { total } = useListContext();

  return (
    <TopToolbar>
      <ClearFiltersButton />
      <FilterButton filters={productFilters} />
      {hasPermission("product.create") && <CreateButton />}
      {hasPermission("product.export") && <ProductsListExport />}
      {hasPermission("product.export") && (
        <ExportButton
          disabled={total === 0}
          maxResults={100_000}
          exporter={exporter}
        />
      )}
      <ProductsImportButton />
    </TopToolbar>
  );
};

export const ProductList = () => {
  const { hasPermission } = useAdmin();

  if (!hasPermission("product.list")) return null;

  return (
    <List
      filters={productFilters}
      sort={{ field: "createdAt", order: "DESC" }}
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[20]} />}
      hasCreate={hasPermission("product.create")}
      actions={<ProductActions />}
      empty={false} // Disable the default empty state
    >
      <ProductListContent />
    </List>
  );
};

const ProductListContent = () => {
  const { filterValues } = useListFilterContext();
  const { hasPermission } = useAdmin();
  const { data: supplier } = useGetOne<TSupplier>(SUPPLIERS, {
    id: filterValues.supplierId,
})

  if (!filterValues.supplierId || !supplier) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 4, md: 6 },
          m: { xs: 2, md: 3 },
          textAlign: "center",
          borderStyle: "dashed",
        }}
      >
        <Stack alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "action.hover",
            }}
          >
            <StoreOutlined sx={{ fontSize: 28, color: "text.secondary" }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Selecciona un proveedor
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
            Para ver los productos, elige un proveedor en el filtro de arriba.
            El filtro <b>Proveedor</b> es obligatorio.
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <ListBase
      resource={PRODUCTS}
      filter={{ ...filterValues, countryCode: supplier.countryCode }}
      sort={{ field: "createdAt", order: "DESC" }}
      perPage={20}
      queryOptions={{ meta: { mode: "search" } }}
    >
      <Datagrid
        rowClick={hasPermission("product.view") ? "show" : undefined}
        bulkActionButtons={false}
        sx={{
          "& .RaDatagrid-headerCell": {
            fontWeight: 600,
            textTransform: "uppercase",
            fontSize: "0.7rem",
            letterSpacing: "0.05em",
            color: "text.secondary",
          },
          "& .column-photo": { width: 80 },
        }}
      >
        <FunctionField
          label="Foto"
          cellClassName="column-photo"
          render={() => (
            <ModalImageField
              source="media.0.src"
              maxHeight={64}
              maxWidth={64}
            />
          )}
        />
        <FunctionField
          label="Producto"
          render={(p: any) => (
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 320,
                }}
              >
                {p.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace" }}
              >
                {p.sku}
              </Typography>
            </Stack>
          )}
        />

        <ReferenceField source="supplierId" reference={SUPPLIERS} link={"show"}>
          <TextField source="name" />
        </ReferenceField>

        <FunctionField
          label="Marca"
          render={(product: any) => product.brand?.name || "-"}
        />

        <NumberField source="wholesalePrice" />
        <FunctionField
          source="status"
          render={() =>
            hasPermission("product.update.status") ? (
              <ProductStatusToggle source="status" />
            ) : (
              <BooleanField source="status" />
            )
          }
        />
      </Datagrid>
      <Pagination rowsPerPageOptions={[20]} />
    </ListBase>
  );
};
